// The WORKSPACE SESSION (2026-09-28): which brand the reader has opened with
// its code. It replaced the build-time REACT_APP_MOCK_BRAND gate — the one
// brand the demo home, the digest, the Lab and the advisor override were
// authored for is now whoever logged in, and the app has a wall
// (components/AuthGate → pages/Login) instead of a flag.
//
// Three states:
//   checking — a stored session exists and is being re-verified with
//              POST /api/auth/verify; the gate shows the app loader
//              (components/AppLoader) so a returning reader never sees
//              the wall flash. The answer lands inside a VIEW TRANSITION
//              (2026-10-15, utils/viewTransition — the same dissolve the
//              wall's welcome opens the gate with): the loader fades
//              while the home rises, never a cut from a spinner to a page
//   out      — no session: the gate renders the PIN screen at whatever URL
//              was asked for (deep links survive the login)
//   in       — the session brand's workspace renders
// Verify logs the reader out ONLY on an explicit 401 (a rotated or deleted
// code); an unreachable API keeps the cached session and lets the pages'
// own error states speak.
//
// A code opens a workspace only when the brand has an authored Lab pack
// (mock/lab): the server knows codes, not packs, so a coded brand with no
// pack is refused HERE with "No workspace is set up for this code yet."
// (the server has already counted that login — accepted, 2026-09-28).
//
// THE BACK-FORWARD CACHE (2026-10-09, user report: "logged in with the
// code from a link, the browser's back returns him to the auth page").
// Nothing in the app ever pushes a wall entry — the gate's scrub and hop
// are replaces, the PIN screen renders in place — so the wall the back
// button showed was a whole DOCUMENT: the one the reader left on the way
// to the link (the wall after a logout, or at "/" before pasting the
// link), which the browser kept in its back-forward cache and restored
// with its React state frozen at `out`, while storage had long held the
// session the link opened. So the provider listens to `pageshow` and, on
// a RESTORED document (`persisted`), re-reads storage and realigns with
// it: a session stored while the document slept → `checking` (the same
// verify a fresh load gets, behind the app loader, so the restored wall
// is replaced before it can be used); storage emptied meanwhile (a
// logout in the other document) → `out`, the cache reset as a logout
// would. A live document never gets a persisted `pageshow`, so the
// running app is untouched. Storage is the one truth of "who is signed
// in on this browser"; the document's memory is never allowed to outrank it.
//
// This is a UI wall, not an API perimeter: the read endpoints stay open,
// the session only decides what the app renders. The stored shape and the
// device id live in services/sessionStore.
//
// `initialSession` is for tests (and nothing else): a preset "in" state
// with no storage read and no network. Components rendered with NO
// provider read the default "out" value — the plain, pack-less board.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useAppData } from "contexts/AppDataContext";
import { labConfig } from "mock/lab";
import { login as loginRequest, verify } from "services/authService";
import {
  clearSession,
  deviceId,
  readSession,
  writeSession,
} from "services/sessionStore";
import { withViewTransition } from "utils/viewTransition";

export const SESSION_CHECKING = "checking";
export const SESSION_OUT = "out";
export const SESSION_IN = "in";

export const NO_WORKSPACE = "No workspace is set up for this code yet.";

const noop = () => {};
const needsProvider = async () => {
  throw new Error("login needs a <SessionProvider>");
};
const DEFAULT = {
  status: SESSION_OUT,
  session: null,
  login: needsProvider,
  admit: needsProvider,
  enter: noop,
  logout: noop,
};

const SessionContext = createContext(DEFAULT);

const fromWire = (out, code) => ({
  slug: out.slug,
  name: out.name,
  industryCode: out.industry_code,
  code,
});

export function SessionProvider({ children, initialSession = undefined }) {
  const { reset } = useAppData();
  const [state, setState] = useState(() => {
    if (initialSession !== undefined) {
      return initialSession
        ? { status: SESSION_IN, session: initialSession }
        : { status: SESSION_OUT, session: null };
    }
    const stored = readSession();
    return stored
      ? { status: SESSION_CHECKING, session: stored }
      : { status: SESSION_OUT, session: null };
  });

  // the one network call a returning reader costs: re-check the stored code
  useEffect(() => {
    if (state.status !== SESSION_CHECKING) return undefined;
    let live = true;
    const stored = state.session;
    // the loader dissolves into whatever the answer is — the app, or the
    // wall for a dead code (see the head of the file)
    const settle = (next) => withViewTransition(() => setState(next));
    verify(stored.slug, stored.code).then(
      (out) => {
        if (!live) return;
        // a brand can be renamed between visits — the wire wins
        const next = fromWire(out, stored.code);
        if (!labConfig(next.slug)) {
          clearSession();
          settle({ status: SESSION_OUT, session: null });
          return;
        }
        writeSession(next);
        settle({ status: SESSION_IN, session: next });
      },
      (err) => {
        if (!live) return;
        if (err?.status === 401) {
          clearSession();
          settle({ status: SESSION_OUT, session: null });
        } else {
          settle({ status: SESSION_IN, session: stored });
        }
      },
    );
    return () => {
      live = false;
    };
  }, [state]);

  // a document restored from the back-forward cache (see the head of the
  // file): storage is the truth, the frozen state is realigned with it.
  // The current state is read through a ref so the listener is bound once
  const stateRef = useRef(state);
  stateRef.current = state;
  useEffect(() => {
    // a preset session is the tests' — it has no storage to realign with
    if (initialSession !== undefined) return undefined;
    const onPageShow = (event) => {
      if (!event.persisted) return;
      const prev = stateRef.current;
      const stored = readSession();
      if (!stored) {
        if (prev.status === SESSION_OUT) return;
        reset();
        setState({ status: SESSION_OUT, session: null });
        return;
      }
      const same =
        prev.status !== SESSION_OUT &&
        prev.session.slug === stored.slug &&
        prev.session.code === stored.code;
      if (same) return;
      // another workspace was opened meanwhile: its cache must not carry over
      if (prev.status === SESSION_IN) reset();
      setState({ status: SESSION_CHECKING, session: stored });
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [initialSession, reset]);

  // The door in TWO steps (2026-10-01, the wall's welcome beat): `admit` is
  // the request and the pack test — the session is STORED, so a reader who
  // closes the tab inside the beat is let in on the next load, but the
  // gate stays shut — and `enter` opens it. The PIN screen holds its
  // "You're in" between the two; `login` is both at once.
  const admit = useCallback(async (code) => {
    const out = await loginRequest(code, deviceId());
    if (!labConfig(out.slug)) {
      const err = new Error(NO_WORKSPACE);
      err.status = 0;
      err.noWorkspace = true;
      throw err;
    }
    const next = fromWire(out, code);
    writeSession(next);
    return next;
  }, []);

  const enter = useCallback((next) => {
    setState({ status: SESSION_IN, session: next });
  }, []);

  const login = useCallback(
    async (code) => {
      const next = await admit(code);
      enter(next);
      return next;
    },
    [admit, enter],
  );

  const logout = useCallback(() => {
    clearSession();
    reset();
    setState({ status: SESSION_OUT, session: null });
  }, [reset]);

  const value = useMemo(
    () => ({
      status: state.status,
      // the session is exposed only once it stands — a checking reader has
      // no workspace yet
      session: state.status === SESSION_IN ? state.session : null,
      login,
      admit,
      enter,
      logout,
    }),
    [state, login, admit, enter, logout],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession() {
  return useContext(SessionContext);
}

// the session brand's slug, or "" — what the old MOCK_BRAND constant was
export function useSessionBrand() {
  return useSession().session?.slug || "";
}
